import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// La papelera. Lo que se borra cae aquí entero —el cliente, sus préstamos y
// todos sus abonos— y se queda un mes, restaurable de un clic.
//
// La pantalla existe para una sola cosa: que se vea desde lejos cuánto tiempo
// le queda a cada uno. Una papelera en la que no se sabe cuándo se vacía no
// sirve de nada, porque nadie se atreve a confiar en ella.

const CAJA = {
  background: 'white', borderRadius: '10px', padding: '16px',
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: '14px',
};

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun',
               'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

// Esta sí es una hora de verdad, no una fecha suelta: el servidor la manda
// marcada como universal y aquí se muestra en la hora de quien mira. Mostrarla
// en universal ponía «borrado el 11 de agosto» a algo borrado el 10 por la
// noche —en Colombia son cinco horas de diferencia—, y quien lo borró hace un
// rato no se reconoce en esa fecha.
const fecha = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
};

// Cuanto menos queda, más grita. Los últimos días en rojo: es la señal de que
// hay que decidir ya.
const reloj = (dias) => {
  if (dias <= 0) return { texto: 'se borra hoy', color: '#b91c1c', fondo: '#fef2f2' };
  if (dias === 1) return { texto: 'queda 1 día', color: '#b91c1c', fondo: '#fef2f2' };
  if (dias <= 7) return { texto: `quedan ${dias} días`, color: '#b45309', fondo: '#fffbeb' };
  return { texto: `quedan ${dias} días`, color: '#15803d', fondo: '#f0fdf4' };
};

function Papelera({ onRefresh }) {
  const [lista, setLista] = useState(null);
  const [dias, setDias] = useState(30);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/papelera')
      .then(r => { setLista(r.data?.clientes || []); setDias(r.data?.dias || 30); })
      .catch(() => setError('No se pudo abrir la papelera'));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const restaurar = async (c) => {
    if (!window.confirm(
      `¿Devolver a ${c.nombre} a la lista de clientes?\n\n` +
      `Vuelve con sus ${c.prestamos} préstamo(s) y sus ${c.pagos} abono(s), ` +
      `tal como estaba.`
    )) return;
    setOcupado(c.id);
    try {
      await axios.post(`/api/papelera/${c.id}/restaurar`);
      cargar();
      if (onRefresh) onRefresh();
      alert(`✅ ${c.nombre} volvió a la lista de clientes.`);
    } catch (err) {
      alert(err.response?.data?.error || ('No se pudo restaurar: ' + err.message));
    }
    setOcupado(null);
  };

  // El borrado definitivo pide escribir el nombre. Un `confirm` de un clic es
  // muy poco para lo único de todo el programa que no tiene vuelta atrás.
  const borrarYa = async (c) => {
    const escrito = window.prompt(
      `BORRAR PARA SIEMPRE a ${c.nombre}.\n\n` +
      `Se van sus ${c.prestamos} préstamo(s) y sus ${c.pagos} abono(s), ` +
      `${moneda(c.abonado)} de abonos registrados. Esto no se puede deshacer.\n\n` +
      `Si está seguro, escriba el nombre tal cual:\n${c.nombre}`
    );
    if (escrito === null) return;
    if (escrito.trim().toUpperCase() !== String(c.nombre || '').trim().toUpperCase()) {
      alert('El nombre no coincide. No se borró nada.');
      return;
    }
    setOcupado(c.id);
    try {
      await axios.delete(`/api/papelera/${c.id}`);
      cargar();
      alert(`${c.nombre} se borró definitivamente.`);
    } catch (err) {
      alert(err.response?.data?.error || ('No se pudo borrar: ' + err.message));
    }
    setOcupado(null);
  };

  if (error) return <div style={{ color: '#b91c1c' }}>{error}</div>;
  if (!lista) return <p style={{ color: '#6b7280' }}>Cargando…</p>;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>🗑️ Papelera</h2>
      <p style={{ color: '#6b7280', marginTop: '-8px' }}>
        Los clientes que borró se guardan aquí {dias} días antes de irse para siempre
      </p>

      {lista.length === 0 ? (
        <div style={{ ...CAJA, textAlign: 'center', color: '#6b7280' }}>
          La papelera está vacía.
          <div style={{ fontSize: '13px', marginTop: '6px' }}>
            Cuando borre un cliente aparece aquí, entero, con sus préstamos y sus
            abonos. Nada se pierde el mismo día.
          </div>
        </div>
      ) : lista.map(c => {
        const r = reloj(c.dias_restantes);
        return (
          <div key={c.id} style={CAJA}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          gap: '12px', flexWrap: 'wrap', alignItems: 'baseline' }}>
              <div>
                <strong style={{ fontSize: '16px' }}>{c.nombre}</strong>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Borrado el {fecha(c.borrado_en)}
                  {c.cedula ? ` · cédula ${c.cedula}` : ''}
                  {c.borrado_por ? ` · por ${c.borrado_por}` : ''}
                </div>
              </div>
              <span style={{ padding: '4px 10px', borderRadius: '999px',
                             background: r.fondo, color: r.color,
                             fontWeight: 700, fontSize: '12px' }}>
                {r.texto}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap',
                          margin: '10px 0', fontSize: '13px', color: '#4b5563' }}>
              <span>{c.prestamos} préstamo{c.prestamos === 1 ? '' : 's'}</span>
              <span>{c.pagos} abono{c.pagos === 1 ? '' : 's'}</span>
              <span>prestado <strong>{moneda(c.prestado)}</strong></span>
              <span>abonado <strong>{moneda(c.abonado)}</strong></span>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button onClick={() => restaurar(c)} disabled={ocupado === c.id}
                      style={{ padding: '8px 14px', borderRadius: '6px', border: 'none',
                               background: '#15803d', color: 'white', fontWeight: 700,
                               fontSize: '13px', cursor: 'pointer' }}>
                ↩️ Devolverlo a la lista
              </button>
              <button onClick={() => borrarYa(c)} disabled={ocupado === c.id}
                      style={{ padding: '8px 14px', borderRadius: '6px',
                               border: '1px solid #fca5a5', background: 'white',
                               color: '#b91c1c', fontSize: '13px', cursor: 'pointer' }}>
                Borrarlo ya, para siempre
              </button>
            </div>
          </div>
        );
      })}

      {lista.length > 0 && (
        <p style={{ fontSize: '13px', color: '#6b7280' }}>
          Al devolverlo vuelve tal como estaba: el mismo cliente, los mismos
          préstamos y los mismos abonos, con sus fechas. Nada hay que volver a
          teclear.
        </p>
      )}
    </div>
  );
}

export default Papelera;
